/**
 * Search Management - Moon Moon Agency
 * Handles search overlay functionality
 */

/*********************************************************************************************
 * 🔍 SEARCH MODULE
 */
const SearchManager = {
	// Local configuration
	config: {
		selectors: {
			searchContainer: '[data-search-container]',
			searchInput: '[data-search-input]',
			searchToggle: '[data-search-toggle]',
			searchClose: '[data-search-close]',
			searchClear: '[data-search-clear]',
			predictiveSearch: '[data-predictive-search]'
		},
		classes: {
			stopScrolling: 'stop-scrolling',
			open: 'mm-open'
		},
		timing: {
			searchFocusDelay: 100,
			searchBlurDelay: 100
		}
	},

	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Initialize the search module
	 */
	init() {
		const search = window.getElement(this.config.selectors.searchContainer);
		if (!search) return;

		this._bindEvents();
		window.log('info', 'SearchManager', 'Initialized');
	},

	/**
	 * Bind all static event listeners (called once at init)
	 */
	_bindEvents() {
		// Close button (background overlay or dedicated close btn)
		const searchCloseBtn = window.getElement(this.config.selectors.searchClose);
		if (searchCloseBtn) {
			searchCloseBtn.addEventListener('click', () => this.hide());
		}

		// Clear button (inside search input)
		const searchClearBtn = window.getElement(this.config.selectors.searchClear);
		if (searchClearBtn) {
			searchClearBtn.addEventListener('click', () => this.hide());
		}

		// Predictive search touchstart — enregistré une seule fois (pas dans show())
		const predictiveSearch = window.getElement(this.config.selectors.predictiveSearch);
		if (predictiveSearch) {
			predictiveSearch.addEventListener('touchstart', () => {
				const searchInput = window.getElement(this.config.selectors.searchInput);
				if (searchInput) {
					setTimeout(() => searchInput.blur(), this.config.timing.searchBlurDelay);
				}
			}, { passive: true });
		}
	},

	/**
	 * Update aria-expanded on all search toggle buttons
	 */
	_updateToggleAria(isOpen) {
		window.getElements(this.config.selectors.searchToggle).forEach((btn) => {
			btn.setAttribute('aria-expanded', String(isOpen));
		});
	},

	/**
	 * Setup focus trap for search overlay
	 */
	setupFocusTrap(searchContainer, triggerElement) {
		// Retire un trap encore actif (double open sans close) : sinon la reference
		// handleKeyDown est ecrasee et l'ancien listener devient impossible a retirer.
		this.removeFocusTrap();

		const focusableElements = searchContainer.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes search
			if (e.key === 'Escape') {
				e.preventDefault();
				this.hide();
				this.focusTrap.triggerElement?.focus();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		window.log('info', 'SearchManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'SearchManager', 'Focus trap removed');
		}
	},

	/**
	 * Show search overlay
	 */
	show(triggerElement) {
		const search = window.getElement(this.config.selectors.searchContainer);
		const searchInput = window.getElement(this.config.selectors.searchInput);

		if (!search || !searchInput) {
			window.log('warn', 'SearchManager', 'Required elements not found');
			return;
		}

		document.documentElement.classList.add(this.config.classes.stopScrolling);
		search.classList.add(this.config.classes.open);
		search.setAttribute('aria-hidden', 'false');
		this._updateToggleAria(true);

		if (window.NavigationManager?.showBackgroundFilter) {
			window.NavigationManager.showBackgroundFilter();
		}

		setTimeout(() => {
			searchInput.focus();
			this.setupFocusTrap(search, triggerElement);
		}, this.config.timing.searchFocusDelay);

		window.emit('global:search-opened');
	},

	/**
	 * Hide search overlay
	 */
	hide(keepFilterActive = false, keepStopScrolling = false) {
		const search = window.getElement(this.config.selectors.searchContainer);
		const searchInput = window.getElement(this.config.selectors.searchInput);

		if (!search || !searchInput) {
			window.log('warn', 'SearchManager', 'Required elements not found for hiding');
			return;
		}

		// Ne retirer le scroll-lock QUE si la recherche était réellement ouverte.
		// Sinon (ex: mmHideSearch appelé à l'ouverture du cart pour fermer d'éventuels
		// overlays) on retirerait .stop-scrolling posée par un AUTRE overlay.
		const wasOpen = search.classList.contains(this.config.classes.open);
		if (wasOpen && !keepStopScrolling) {
			document.documentElement.classList.remove(this.config.classes.stopScrolling);
		}
		search.classList.remove(this.config.classes.open);
		search.setAttribute('aria-hidden', 'true');
		this._updateToggleAria(false);

		this.removeFocusTrap();

		if (!keepFilterActive && window.NavigationManager?.hideBackgroundFilter) {
			window.NavigationManager.hideBackgroundFilter();
		}

		searchInput.value = '';
		searchInput.dispatchEvent(new Event('input'));

		window.emit('global:search-closed', { keepFilterActive, keepStopScrolling });
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.SearchManager = SearchManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmShowSearch = (triggerElement) => SearchManager.show(triggerElement);
window.mmHideSearch = (keepFilter, keepScrolling) => SearchManager.hide(keepFilter, keepScrolling);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => SearchManager.init());
} else {
	SearchManager.init();
}
